import React from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Divider,
    Grid
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Person as PersonIcon,
    AdminPanelSettings as AdminIcon,
    Computer as SystemIcon,
    Info as InfoIcon,
    Restore as RestoreIcon,
    Update as UpdateIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';

const ChangeLogDetails = ({ changeLogs, isLoading }) => {
    const getActionIcon = (action) => {
        switch (action) {
            case 'created':
                return <AddIcon color="success" />;
            case 'updated':
                return <UpdateIcon color="info" />;
            case 'deleted':
                return <DeleteIcon color="error" />;
            case 'restored':
                return <RestoreIcon color="warning" />;
            default:
                return <InfoIcon />;
        }
    };

    const getActionColor = (action) => {
        switch (action) {
            case 'created':
                return 'success';
            case 'updated':
                return 'info';
            case 'deleted':
                return 'error';
            case 'restored':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getUserTypeIcon = (userType) => {
        switch (userType) {
            case 'Admin':
                return <AdminIcon fontSize="small" />;
            case 'User':
                return <PersonIcon fontSize="small" />;
            default:
                return <SystemIcon fontSize="small" />;
        }
    };

    const formatFieldName = (field) => {
        return field
            .replace(/smmpanel_/g, '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    const renderFieldChanges = (log) => {
        if (!log.changed_fields || log.changed_fields.length === 0) {
            return (
                <Typography variant="body2" color="textSecondary">
                    No specific field changes recorded
                </Typography>
            );
        }

        return (
            <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Changed Fields:
                </Typography>
                <Grid container spacing={1}>
                    {log.changed_fields.map((field, index) => (
                        <Grid item key={index}>
                            <Chip
                                label={formatFieldName(field)}
                                size="small"
                                variant="outlined"
                                color="primary"
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    };

    const renderDetailedChanges = (log) => {
        if (!log.old_values || !log.new_values || Object.keys(log.old_values).length === 0) {
            return null;
        }

        return (
            <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">View Detailed Changes</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Field</TableCell>
                                    <TableCell>Old Value</TableCell>
                                    <TableCell>New Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(log.old_values).map(([field, oldValue]) => (
                                    <TableRow key={field}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatFieldName(field)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={String(oldValue || 'Empty')}
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={String(log.new_values[field] || 'Empty')}
                                                size="small"
                                                color="primary"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>
        );
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading change logs...
                </Typography>
            </Box>
        );
    }

    if (!changeLogs || changeLogs.length === 0) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                No change logs available for this panel.
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Change History ({changeLogs.length} entries)
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Track all modifications made to this panel by users and admins
            </Typography>

            <Box sx={{ mt: 2 }}>
                {changeLogs.map((log, index) => (
                    <Box key={log.id} sx={{ mb: 2 }}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Tooltip title={log.action}>
                                            <IconButton size="small">
                                                {getActionIcon(log.action)}
                                            </IconButton>
                                        </Tooltip>
                                        <Chip
                                            label={log.action.toUpperCase()}
                                            color={getActionColor(log.action)}
                                            size="small"
                                        />
                                        <Typography variant="body2" color="textSecondary">
                                            {log.changed_at_formatted || new Date(log.changed_at).toLocaleString()}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        {log.changed_by && (
                                            <>
                                                <Tooltip title={`${log.changed_by.type}: ${log.changed_by.name}`}>
                                                    <Chip
                                                        icon={getUserTypeIcon(log.changed_by.type)}
                                                        label={log.changed_by.name}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Tooltip>
                                            </>
                                        )}
                                    </Box>
                                </Box>

                                {log.ip_address && (
                                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                                        IP: {log.ip_address}
                                    </Typography>
                                )}

                                {renderFieldChanges(log)}
                                {renderDetailedChanges(log)}
                            </CardContent>
                        </Card>
                        {index < changeLogs.length - 1 && <Divider sx={{ my: 1 }} />}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default ChangeLogDetails;